import { computed, markRaw, ref, type Component } from "vue";
import type { IconName } from "@/services/icons";

/**
 * The global right-hand sidepanel: one singleton state (which tab, is it
 * visible, how wide) plus a mode REGISTRY. Every surface — AI chat,
 * notifications, tasks, annotations, the homepage block library — is a
 * registered mode; nothing is special-cased in the host.
 *
 * Global modes are defined once (modes.ts) and always available. Contextual
 * modes exist only while at least one provider is registered (identity Set,
 * so concurrent providers and resets can't corrupt availability).
 */

export interface SidepanelModeDef {
  id: string;
  title: string | (() => string);
  /** Canonical position — contextual modes come first, then globals. */
  order: number;
  /** Plain tab icon; null means `tabIcon` supplies a custom component. */
  icon: IconName | null;
  /** Custom tab visual (props: active, size); inherits the host's tab tint. */
  tabIcon?: Component;
  /** Overlay-header icon; defaults to `icon`; null renders the Fyr AI logo. */
  headerIcon?: IconName | null;
  /** Mode body, rendered by the host inside the slide transition. */
  component: Component;
  /** Extra classes on the body wrapper (scroll behavior differs per mode). */
  contentClass?: string;
  /** Mode-specific header/secondary-row actions (e.g. AI chat history). */
  headerActions?: Component;
  /**
   * Rendered once outside the docked/overlay branches while the mode is
   * available, so state such as an open modal survives a layout swap.
   */
  persistent?: Component;
  /** Runs when the mode is opened (e.g. AI derives its prompt/chat phase). */
  onOpen?: () => void;
}

const modeDefs = ref(new Map<string, SidepanelModeDef>());
const providers = ref(new Map<string, Set<symbol>>());
const modeEnabled = ref(new Map<string, boolean>());

function providerSet(id: string): Set<symbol> {
  let set = providers.value.get(id);
  if (!set) {
    set = new Set();
    providers.value.set(id, set);
  }
  return set;
}

/** Define a mode. Global modes (contextual: false) are always available. */
export function defineSidepanelMode(
  def: SidepanelModeDef,
  { contextual = false } = {},
): void {
  modeDefs.value.set(def.id, {
    ...def,
    component: markRaw(def.component),
    tabIcon: def.tabIcon && markRaw(def.tabIcon),
    headerActions: def.headerActions && markRaw(def.headerActions),
    persistent: def.persistent && markRaw(def.persistent),
  });
  if (!contextual) {
    providerSet(def.id).add(Symbol("global"));
  }
}

/** Contextual availability: register while mounted; dispose exactly once. */
export function registerSidepanelProvider(id: string): () => void {
  const key = Symbol();
  providerSet(id).add(key);
  return () => providerSet(id).delete(key);
}

export function setSidepanelModeEnabled(id: string, enabled: boolean): void {
  modeEnabled.value.set(id, enabled);
}

// Portal outlets: views can own a mode's content and <Teleport> it into a
// SidepanelPortalOutlet body. The view gates its Teleport on outlet presence
// so it never targets a missing element.
const portalOutlets = ref(new Set<string>());

export function registerPortalOutlet(id: string): () => void {
  portalOutlets.value.add(id);
  return () => portalOutlets.value.delete(id);
}

export function isPortalOutletMounted(id: string) {
  return computed(() => portalOutlets.value.has(id));
}

export function registerAnnotationProvider(): () => void {
  return registerSidepanelProvider("annotations");
}
export function registerBlockLibraryProvider(): () => void {
  return registerSidepanelProvider("blocks");
}

const availableModes = computed(() =>
  [...modeDefs.value.values()]
    .filter((def) => isModeAvailable(def.id))
    .sort((a, b) => a.order - b.order),
);

function isModeAvailable(id: string): boolean {
  return (
    modeDefs.value.has(id) &&
    (providers.value.get(id)?.size ?? 0) > 0 &&
    (modeEnabled.value.get(id) ?? true)
  );
}

// "Which tab" and "is the panel visible" are separate facts: the docked
// presentation is always visible (it renders selectedMode and ignores
// isOpen), the overlay shows only when isOpen.
const selectedMode = ref("ai");
const isOpen = ref(false);
const isResizing = ref(false);
// See-through, not closed, during drags: display:none would kill a drag started in the panel.
const isSuspended = ref(false);
const DEFAULT_PANEL_WIDTH = 448;
const panelWidth = ref(DEFAULT_PANEL_WIDTH);
// Bumped on every reset; keys persistent mode components so org switch and
// logout discard their state (e.g. an open draft bound to the old org).
const resetGeneration = ref(0);
const panelMaxWidth = computed(() => `${panelWidth.value}px`);
// The overlay auto-open is a once-per-login nudge. The claim records the auth
// revision, which every login and logout bumps, so remounts, reloads and other
// tabs of the same login skip it and the next login re-arms it. The in-memory
// copy covers a page load whose storage write failed.
const AI_AUTO_OPEN_KEY = "sidepanel-ai-auto-opened";
let aiAutoOpenRevision: number | null = null;
function storedAiAutoOpenRevision(): number | null {
  try {
    const stored = localStorage.getItem(AI_AUTO_OPEN_KEY);
    return stored === null ? null : Number(stored);
  } catch {
    return null;
  }
}
export function claimAiAutoOpen(authRevision: number): boolean {
  if (
    // Object.is: a corrupted authRevision key reads as NaN, which === never matches.
    Object.is(aiAutoOpenRevision, authRevision) ||
    Object.is(storedAiAutoOpenRevision(), authRevision)
  ) {
    return false;
  }
  aiAutoOpenRevision = authRevision;
  try {
    localStorage.setItem(AI_AUTO_OPEN_KEY, String(authRevision));
  } catch {
    // Blocked or full storage: the in-memory claim holds until a reload.
  }
  return true;
}

// Suppresses the slide animation while one right panel replaces another
// (edit-dashboard's tab-style switches between the Widgets panel and this
// one) so the swap is instant instead of a fly-out + fly-in.
export const panelSwitching = ref(false);
let panelSwitchTimer: ReturnType<typeof setTimeout> | null = null;
export function markPanelSwitch(): void {
  panelSwitching.value = true;
  if (panelSwitchTimer) clearTimeout(panelSwitchTimer);
  panelSwitchTimer = setTimeout(() => {
    panelSwitching.value = false;
  }, 400);
}

// Counts deliberate opens. isOpen can't tell them apart from docking, and
// stays true while the docked panel switches modes.
let openRequests = 0;

export function sidepanelOpenRequests(): number {
  return openRequests;
}

function openMode(target: string): void {
  if (!isModeAvailable(target)) return;
  openRequests++;
  modeDefs.value.get(target)?.onOpen?.();
  selectedMode.value = target;
  isOpen.value = true;
}

function close(): void {
  isOpen.value = false;
}

function isModeOpen(id: string) {
  return computed(() => isOpen.value && selectedMode.value === id);
}

/** Close X wherever it shows: overlay closes; the docked presentation
 * (which ignores isOpen) falls back to the AI tab. For "this content is no
 * longer available" teardowns. */
export function closeSidepanelMode(target: string): void {
  if (selectedMode.value !== target) return;
  selectedMode.value = "ai";
  isOpen.value = false;
}

/** Reset panel state. Call on logout and organization switch.
 *
 * Mode defs and provider registries are deliberately NOT cleared: org switch
 * doesn't unmount the current view, so its providers are still live and own
 * their entries. Logout empties contextual providers via unmounts. */
export function resetSidepanelState(): void {
  selectedMode.value = "ai";
  isOpen.value = false;
  panelWidth.value = DEFAULT_PANEL_WIDTH;
  isResizing.value = false;
  isSuspended.value = false;
  resetGeneration.value++;
}

export function useSidepanel() {
  return {
    selectedMode,
    isOpen,
    availableModes,
    modeDefs,
    isModeAvailable,
    isModeOpen,
    openMode,
    close,
    panelWidth,
    panelMaxWidth,
    isResizing,
    isSuspended,
    resetGeneration,
  };
}
